<script setup lang="ts">
import { useOnboarding } from '@/composables/useOnboarding';
import { useUserStore } from '@/stores/useUserStore';
import Avatar from 'primevue/avatar';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Dialog from 'primevue/dialog';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// 1. On récupère tout depuis le composable
const {
  startTour,
  getProgress, // Renvoie maintenant la computed basée sur le store
  navigateToChapter,
  getCurrentChapter,
  fetchOnboardingStatus,
  completeOnboardingAPI, // Correctly imported now
  dismissOnboardingAPI, // For "do not show again" feature
  getDisplayableChaptersForRole, // Fonction pour les chapitres affichables
  isOnboardingActive, // Ajout pour vérifier si l'onboarding est actif
  shouldShowOnboardingDialog, // Signal pour réafficher le dialog
} = useOnboarding();

const userStore = useUserStore();
const { t } = useI18n();

const visible = ref(false);
const isStarting = ref(false);
const doNotShowAgain = ref(false);
const isDismissing = ref(false);

// 2. PLUS D'ÉTAT LOCAL ! On utilise des computed qui réagissent au UserStore
// getProgress() dans le composable lit le store. Si le store change, ceci change.
const completedChapters = computed(() => getProgress());

const currentChapter = computed(() => getCurrentChapter());

// Update the computed property `isFullyCompleted` to ensure it checks both `onboarding_complete` and the progress
const isFullyCompleted = computed(() => {
  const progress = completedChapters.value;
  const displayableChapters = chapters.value.map((c) => c.key);
  const completedDisplayableChapters = progress.filter((chapterKey) => displayableChapters.includes(chapterKey));
  return !!userStore.user?.onboarding_complete || completedDisplayableChapters.length === displayableChapters.length;
});

const hasStarted = computed(() => {
  const result = completedChapters.value.length > 0 && !isFullyCompleted.value;
  console.log('[OnboardingDialog] hasStarted:', result, {
    completedChapters: completedChapters.value.length,
    isFullyCompleted: isFullyCompleted.value,
  });
  return result;
});

// Fonction pour vérifier et afficher le dialog si nécessaire
// Computed pour déterminer si le dialog doit être affiché
const dialogVisible = computed({
  get() {
    return visible.value && !isOnboardingActive.value;
  },
  set(value) {
    visible.value = value;
  },
});

function checkAndShowDialog() {
  console.log('[OnBoardingDialog] checkAndShowDialog:', {
    onboarding_complete: userStore.user?.onboarding_complete,
    onboarding_dismissed: userStore.user?.onboarding_dismissed,
    visible: visible.value,
  });

  // Check if user has dismissed the onboarding (from backend)
  const isDismissed = userStore.user?.onboarding_dismissed === true;

  if (userStore.user?.onboarding_complete === false && !visible.value && !isDismissed) {
    console.log('[OnBoardingDialog] Affichage du dialog...');
    setTimeout(() => {
      visible.value = true;
    }, 500);
  }
}

// Watch pour surveiller les changements d'état de l'onboarding
watch(
  () => userStore.user?.onboarding_complete,
  (newValue, oldValue) => {
    console.log('[OnBoardingDialog] Watch triggered:', {
      oldValue,
      newValue,
      visible: visible.value,
      completedChapters: completedChapters.value,
    });

    if (newValue === false) {
      checkAndShowDialog();
    } else if (newValue === true) {
      visible.value = false;
    }
  },
);

// Tous les chapitres possibles
const allChapters = [
  {
    key: '01_Introduction',
    title: t('onboarding.chapters.introduction.title'),
    description: t('onboarding.chapters.introduction.description'),
  },
  {
    key: '02_RecipeGeneration',
    title: t('onboarding.chapters.recipeGeneration.title'),
    description: t('onboarding.chapters.recipeGeneration.description'),
  },
  {
    key: '03_RecipeDetails',
    title: t('onboarding.chapters.recipeDetails.title'),
    description: t('onboarding.chapters.recipeDetails.description'),
  },
  {
    key: '04_RecipeActions',
    title: t('onboarding.chapters.recipeActions.title'),
    description: t('onboarding.chapters.recipeActions.description'),
  },
  {
    key: '05_Organization',
    title: t('onboarding.chapters.organization.title'),
    description: t('onboarding.chapters.organization.description'),
  },
  {
    key: '06_Scan',
    title: t('onboarding.chapters.scan.title'),
    description: t('onboarding.chapters.scan.description'),
  },
  {
    key: '07_ProfileAndTeam',
    title: t('onboarding.chapters.profileAndTeam.title'),
    description: t('onboarding.chapters.profileAndTeam.description'),
  },
  {
    key: '08_Planning',
    title: t('onboarding.chapters.planning.title'),
    description: t('onboarding.chapters.planning.description'),
  },
];

// Filtrer les chapitres selon le rôle de l'utilisateur
const chapters = computed(() => {
  const displayableKeys = getDisplayableChaptersForRole(userStore.contextualRole || 'admin');
  return allChapters.filter((chapter) => displayableKeys.includes(chapter.key));
});

onMounted(async () => {
  // Logique simple : afficher le dialog seulement si onboarding_complete === false
  try {
    // S'assurer que les données utilisateur sont à jour
    await fetchOnboardingStatus();

    // Vérifier et afficher le dialog
    checkAndShowDialog();
  } catch (e) {
    console.error('Erreur init onboarding dialog', e);
  }
});

async function resumeOrStart() {
  visible.value = false;
  isStarting.value = true;

  // Nettoyage des overlays PrimeVue résiduels
  document.querySelectorAll('.p-component-overlay').forEach((el) => el.remove());

  // Lancement via le composable
  await startTour();

  isStarting.value = false;
}

async function goToChapter(chapterKey: string) {
  visible.value = false;
  isStarting.value = true;

  try {
    document.querySelectorAll('.p-component-overlay').forEach((el) => el.remove());
    await navigateToChapter(chapterKey);
  } catch (error) {
    console.error('[OnBoardingDialog] Erreur navigation:', error);
    visible.value = true;
  } finally {
    isStarting.value = false;
  }
}

async function skipOnboarding() {
  if (doNotShowAgain.value) {
    isDismissing.value = true;
    try {
      // Save the "do not show again" preference to backend
      await dismissOnboardingAPI();
      visible.value = false;
    } catch (error) {
      console.error('[OnboardingDialog] Error dismissing onboarding:', error);
      // Still close the dialog even if API fails
      visible.value = false;
    } finally {
      isDismissing.value = false;
    }
  } else {
    visible.value = false;
  }
}

// Helpers pour l'interface (utilisent maintenant les computed)
function isCompleted(chapterKey: string) {
  return completedChapters.value.includes(chapterKey);
}

function isCurrent(chapterKey: string) {
  // Si Shepherd est actif sur ce chapitre
  if (currentChapter.value === chapterKey) return true;

  // Sinon, logique "chapitre suivant à faire"
  const index = chapters.value.findIndex((c) => c.key === chapterKey);
  if (index > 0) {
    const prevKey = chapters.value[index - 1].key;
    return isCompleted(prevKey) && !isCompleted(chapterKey);
  }
  // Cas du premier chapitre
  return !isCompleted(chapterKey) && completedChapters.value.length === 0;
}

async function completeOnboarding() {
  try {
    // Marquer le chapitre de fin déclenche l'appel /complete dans le composable
    await completeOnboardingAPI();
    visible.value = false; // Fermer le dialog après la complétion
    // Le store sera mis à jour automatiquement par le composable
  } catch (error) {
    console.error('[OnBoardingDialog] Error completing onboarding:', error);
  }
}

// Watcher pour réafficher le dialog quand demandé par useOnboarding
watch(shouldShowOnboardingDialog, (newValue) => {
  if (newValue) {
    console.log('[OnboardingDialog] Signal reçu pour réafficher le dialog');
    visible.value = true;
    shouldShowOnboardingDialog.value = false; // Reset
  }
});
</script>

<template>
  <Dialog v-model:visible="dialogVisible" modal :draggable="false" :closable="false" :dismissable-mask="false" class="w-11/12 md:w-1/2 lg:w-1/3">
    <template #header>
      <div class="flex w-full justify-center">
        <div class="dialog-header">
          <i class="pi pi-compass text-primary mb-3 text-4xl"></i>
          <h2 class="m-0">{{ t('onboarding.dialog.welcomeTitle') }}</h2>
        </div>
      </div>
    </template>

    <div class="dialog-content">
      <div v-if="!hasStarted">
        <p class="mb-4 text-lg">{{ t('onboarding.dialog.welcomeDescription') }}</p>

        <div class="features-list mb-4">
          <div class="feature-item">
            <i class="pi pi-check-circle text-green-500"></i>
            <span>{{ t('onboarding.dialog.features.createRecipes') }}</span>
          </div>

          <div class="feature-item">
            <i class="pi pi-check-circle text-green-500"></i>
            <span>{{ t('onboarding.dialog.features.organizeFolders') }}</span>
          </div>

          <div class="feature-item">
            <i class="pi pi-check-circle text-green-500"></i>
            <span>{{ t('onboarding.dialog.features.scanRecipes') }}</span>
          </div>

          <div class="feature-item">
            <i class="pi pi-check-circle text-green-500"></i>
            <span>{{ t('onboarding.dialog.features.planMenus') }}</span>
          </div>
        </div>

        <!-- Message d'information sur la progression -->
        <div class="mb-4 border-l-4 border-blue-400 bg-blue-50 p-3 text-left">
          <p class="m-0 text-sm text-blue-800">
            {{ t('onboarding.dialog.progressInfo') }}
          </p>
        </div>

        <!-- Checkbox for "Do not show again" when onboarding hasn't started -->
        <div class="mb-4 flex items-center">
          <Checkbox v-model="doNotShowAgain" input-id="doNotShowOnboardingAgain" class="p-checkbox-sm" binary />
          <label for="doNotShowOnboardingAgain" class="ml-2 text-sm"> {{ t('onboarding.dialog.dontShowAgain') }} </label>
        </div>
      </div>

      <div v-else>
        <p class="mb-4 text-lg">{{ t('onboarding.dialog.resumeTitle') }}</p>

        <div class="chapters-grid mb-4">
          <div
            v-for="(chapter, index) in chapters"
            :key="chapter.key"
            class="chapter-card"
            :class="{
              completed: isCompleted(chapter.key),
              current: isCurrent(chapter.key),
            }"
            @click="goToChapter(chapter.key)"
          >
            <Avatar :label="`${index + 1}`" class="chapter-number" shape="circle" />
            <div class="chapter-info">
              <h4>{{ chapter.title }}</h4>
              <p>{{ chapter.description }}</p>
            </div>
            <div class="chapter-status">
              <Avatar v-if="isCompleted(chapter.key)" class="status completed" icon="pi pi-check" shape="circle" />
              <Avatar v-else-if="isCurrent(chapter.key)" icon="pi pi-play" class="status current" shape="circle" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <Button :label="t('onboarding.dialog.laterButton')" text icon="pi pi-times" severity="secondary" @click="skipOnboarding" />

        <Button
          :label="hasStarted ? t('onboarding.dialog.resumeButton') : t('onboarding.dialog.startButton')"
          :loading="isStarting"
          icon="pi pi-arrow-right"
          icon-pos="right"
          @click="resumeOrStart"
        />

        <Button
          v-if="hasStarted"
          :label="t('onboarding.dialog.completeButton')"
          icon="pi pi-check"
          icon-pos="right"
          class="ml-auto rounded"
          @click="completeOnboarding"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.dialog-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 1rem;
}

.dialog-content {
  text-align: center;
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Styles pour la navigation des chapitres */
.chapters-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
  max-height: 300px;
  overflow-y: auto;
}

.chapter-card {
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.chapter-card:hover {
  border-color: #3b82f6;
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.chapter-card.completed {
  border-color: #10b981;
  background: #f0fdf4;
}

.chapter-card.current {
  border-color: #3b82f6;
  background: #eff6ff;
}

.chapter-number {
  background: #e5e7eb;
  font-weight: 600;
  color: #6b7280;
  margin-right: 0.75rem;
}

.chapter-card.completed .chapter-number {
  background: #10b981;
  color: white;
}

.chapter-card.current .chapter-number {
  background: #3b82f6;
  color: white;
}

.chapter-info {
  flex: 1;
}

.chapter-info h4 {
  margin: 0 0 0.25rem 0;
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
}

.chapter-info p {
  margin: 0;
  color: #6b7280;
  font-size: 0.75rem;
  line-height: 1.3;
}

.chapter-status {
  margin-left: 0.75rem;
  flex-shrink: 0;
}

.status {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
}

.status.completed {
  background: #10b981;
  color: white;
}

.status.current {
  background: #3b82f6;
  color: white;
}

.status.available {
  background: #e5e7eb;
  color: #6b7280;
}

/* Responsive design */
@media (max-width: 768px) {
  .chapter-card {
    padding: 0.75rem;
  }

  .chapter-number {
    width: 28px;
    height: 28px;
    margin-right: 0.5rem;
    font-size: 0.75rem;
  }

  .chapter-info h4 {
    font-size: 0.8rem;
  }

  .chapter-info p {
    font-size: 0.7rem;
  }

  .status {
    width: 18px;
    height: 18px;
    font-size: 9px;
  }

  .chapters-grid {
    max-height: 250px;
  }
}
</style>
